/**
 * Controller for UI functionality
 */
import { CONFIG } from '../config/config.js';
import { AppState } from './appState.js';
import { formatPopulation } from '../utils/formatter.js';
import { findNearestDataSigma as findNearestSigma } from '../utils/sigmaUtils.js';

export class UIController {
  constructor(mapController) {
    this.mapController = mapController;

    // Ensure DOM is fully loaded before setting up UI
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupUIElements();
        this.setupEventHandlers();
      });
    } else {
      this.setupUIElements();
      this.setupEventHandlers();
    }
  }

  /**
   * Set up UI elements
   */
  setupUIElements() {
    this.setupSigmaControls();
    this.initializeSliders();
    this.setupTimeSlider();

    this.setupStepControl();

    // Setup collapsible panels last to ensure all DOM elements are ready
    this.setupCollapsiblePanels();
  }

  /**
   * Create sigma value controls
   */
  setupSigmaControls() {
    // Hidden controls removed for performance.
    // State is now managed directly in AppState.sigmaOpacities.
  }

  /**
   * Initialize slider values
   */
  initializeSliders() {
    // No longer needed: sliders for each sigma removed.
  }

  /**
   * Set up collapsible panels
   */
  setupCollapsiblePanels() {
    const collapsibleHeaders = document.querySelectorAll('.collapsible-header');

    // First remove any existing listeners to avoid duplicates
    collapsibleHeaders.forEach(header => {
      const newHeader = header.cloneNode(true);
      header.parentNode.replaceChild(newHeader, header);
    });

    // Now add fresh event listeners
    document.querySelectorAll('.collapsible-header').forEach(header => {
      // Skip population header as it's handled separately
      if (header.id === 'poi-header') return;

      header.addEventListener('click', function () {
        // Toggle the active class on the header
        this.classList.toggle('active');

        // Get the content panel that follows this header
        const content = this.nextElementSibling;

        // Toggle visibility of the content
        if (content.style.maxHeight) {
          content.style.maxHeight = null;
        } else {
          content.style.maxHeight = content.scrollHeight + "px";
        }
      });
    });

    // Initialize panel states based on AppState.expandedPanels
    Object.entries(AppState.expandedPanels).forEach(([panelKey, isExpanded]) => {
      let headerId;
      // Map panelKey to the corresponding header ID
      if (panelKey === 'yearSlider') headerId = 'year-slider-header';
      else if (panelKey === 'watershedLayers') headerId = 'watershed-layers-header';
      else if (panelKey === 'population') headerId = 'poi-header'; // Use poi-header
      else if (panelKey === 'referenceLayers') headerId = 'reference-layers-header';
      else if (panelKey === 'basemaps') headerId = 'basemaps-header';

      if (headerId) {
        const header = document.getElementById(headerId);
        const content = header?.nextElementSibling;

        if (header && content && isExpanded) {
          // Special handling for population/POI panel
          if (headerId === 'poi-header') {
            const poiContent = document.getElementById('poi-content');
            if (poiContent) {
              header.classList.add('active');
              poiContent.classList.add('expanded');
            }
          } else {
            // Standard handling for other panels
            header.classList.add('active');
            setTimeout(() => {
              content.style.maxHeight = content.scrollHeight + "px";
            }, 0);
          }
        } else if (header && !isExpanded) {
          header.classList.remove('active');
          if (content && headerId !== 'poi-header') {
            content.style.maxHeight = null;
          } else if (headerId === 'poi-header') {
            const poiContent = document.getElementById('poi-content');
            if (poiContent) poiContent.classList.remove('expanded');
          }
        }
      }
    });
  }

  /**
   * Set up time slider controls
   */
  setupTimeSlider() {
    const sigmaSlider = document.getElementById('sigma-slider'); // Corrected ID
    const sigmaDisplay = document.getElementById('sigma-display'); // Corrected ID

    // Time slider functionality now relies on AppState.sigmaOpacities

    // Initialize with the current slider value
    if (sigmaSlider) { // Add null check
      AppState.sigmaValue = parseFloat(sigmaSlider.value);
      this.updateLayersBySigma(AppState.sigmaValue);
    } else {
      console.error("Sigma slider element not found for setupTimeSlider");
    }
  }



  /**
   * Set up step control
   */
  setupStepControl() {
    const stepSelect = document.getElementById('sigma-step');
    const sigmaSlider = document.getElementById('sigma-slider');

    if (stepSelect && sigmaSlider) {
      stepSelect.addEventListener('change', (e) => {
        const step = e.target.value;
        sigmaSlider.step = step;
      });
    }
  }


  /**
   * Set up all event handlers
   */
  setupEventHandlers() {
    // Basemap handlers
    this.setupBasemapHandlers();

    // Layer type handlers
    this.setupLayerTypeHandlers();

    // Sigma toggle handlers
    this.setupSigmaToggleHandlers();

    // POI slider handlers
    this.setupPoiHandler();

    // Time slider handlers
    this.setupTimeSliderHandlers();

    // Cell size selector handler removed


    // Preset selector handler
    this.setupPresetHandler();

    // Location search handlers
    this.setupLocationSearchHandlers();

    // Sigma scale handler
    this.setupSigmaScaleHandler();

    // Direct handler for POI header
    const poiHeader = document.getElementById('poi-header');
    const poiContent = document.getElementById('poi-content');

    if (poiHeader && poiContent) {
      // Remove previous listeners by cloning and replacing
      const newHeader = poiHeader.cloneNode(true);
      poiHeader.parentNode.replaceChild(newHeader, poiHeader);

      // Add fresh click handler
      newHeader.addEventListener('click', () => {
        // Toggle the active class on header
        newHeader.classList.toggle('active');

        // Toggle expanded class on content
        poiContent.classList.toggle('expanded');
      });
    } else {
      console.error('Could not find POI header or content');
    }
  }

  /**
   * Set up basemap control handlers
   */
  setupBasemapHandlers() {
    const basemapIds = ['osm-basemap', 'carto-basemap', 'esri-imagery', 'esri-streetmap', 'esri-topo', 'esri-relief'];

    basemapIds.forEach(id => {
      document.getElementById(id).addEventListener('change', (e) => {
        if (e.target.checked) {
          this.mapController.hideAllBasemaps();

          // Map the HTML ID to the correct config key
          let basemapKey;
          if (id === 'osm-basemap') {
            basemapKey = 'osm';
          } else if (id === 'carto-basemap') {
            basemapKey = 'carto';
          } else if (id === 'esri-imagery') {
            basemapKey = 'esriImagery';
          } else if (id === 'esri-streetmap') {
            basemapKey = 'esriStreetMap';
          } else if (id === 'esri-topo') {
            basemapKey = 'esriTopo';
          } else if (id === 'esri-relief') {
            basemapKey = 'esriRelief';
          }

          if (basemapKey && this.mapController.basemapLayers[basemapKey]) {
            this.mapController.basemapLayers[basemapKey].setVisible(true);
          } else {
            console.error(`Basemap layer not found for key: ${basemapKey}`);
          }
        }
      });
    });
  }

  /**
   * Set up layer type control handlers
   */
  setupLayerTypeHandlers() {
    document.getElementById('boundary-toggle').addEventListener('change', () => {
      this.mapController.updateAllLayersOpacity();
    });

    document.getElementById('points-toggle').addEventListener('change', () => {
      this.mapController.updateAllLayersOpacity();
    });

    // Handle reference layer radio buttons in a more maintainable way
    const referenceRadios = document.querySelectorAll('input[name="reference-layer"]');
    referenceRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Find which reference layer key this radio button corresponds to
          const layerKey = Object.keys(CONFIG.referenceLayers).find(key =>
            CONFIG.referenceLayers[key].id === e.target.id
          );

          if (layerKey) {
            // Use the centralized method to update visibility
            this.mapController.setReferenceLayerVisibility(layerKey);
          } else {
            console.error(`No reference layer found for ID: ${e.target.id}`);
          }
        }
      });
    });
  }

  /**
   * Set up sigma toggle control handlers
   */
  setupSigmaToggleHandlers() {
    // Sigma toggles and individual opacity sliders removed for performance.
  }

  /**
   * Set up POI slider handlers
   */
  setupPoiHandler() {
    const linearSlider = document.getElementById('poi-slider');
    const logSlider = document.getElementById('poi-slider-log');
    const linearScale = document.getElementById('linear-scale');
    const logScale = document.getElementById('log-scale');

    // Standard logarithmic scale conversion functions - make them available on the instance
    this.linearToLog = (value) => {
      const linearValue = parseInt(value, 10);
      if (linearValue === 0) return 0;
      return Math.log10(linearValue + 1);
    };

    this.logToLinear = (value) => {
      const logValue = parseFloat(value);
      if (logValue === 0) return 0;
      return Math.round(Math.pow(10, logValue) - 1);
    };

    // Event listeners for linear slider
    linearSlider.addEventListener('input', () => {
      // Update the logarithmic slider to match
      logSlider.value = this.linearToLog(linearSlider.value);

      // Only update filters if linear scale is selected
      if (linearScale.checked) {
        this.updatePoiValue();
      }
    });

    // Event listeners for logarithmic slider
    logSlider.addEventListener('input', () => {
      // Update the linear slider to match
      const linearValue = this.logToLinear(logSlider.value);
      linearSlider.value = linearValue;

      // Only update filters if log scale is selected
      if (logScale.checked) {
        this.updatePoiValue();
      }
    });

    // Toggle between linear and logarithmic scales
    linearScale.addEventListener('change', () => {
      if (linearScale.checked) {
        linearSlider.style.display = 'block';
        logSlider.style.display = 'none';

        // Update the App State
        AppState.poiScale = 'linear';
      }
    });

    logScale.addEventListener('change', () => {
      if (logScale.checked) {
        linearSlider.style.display = 'none';
        logSlider.style.display = 'block';

        // When switching to log scale, ensure the log slider has a proper value
        logSlider.value = this.linearToLog(linearSlider.value);

        // Update the App State
        AppState.poiScale = 'logarithmic';
      }
    });

    // Initialize with logarithmic slider
    linearSlider.style.display = 'none';
    logSlider.style.display = 'block';

    // Make sure log scale is checked (should already be from HTML)
    logScale.checked = true;
    linearScale.checked = false;

    // Set initial App State
    AppState.poiScale = 'logarithmic';

    // Initial update to set the filters
    this.updatePoiValue();
  }

  /**
   * Linear to logarithmic mapping for sigma
   */
  sigmaLinearToLog(value) {
    const linearValue = parseFloat(value);
    // Map 1-500 to 0-2.699 (log10(500))
    return Math.log10(linearValue);
  }

  /**
   * Logarithmic to linear mapping for sigma
   */
  sigmaLogToLinear(value) {
    const logValue = parseFloat(value);
    return Math.round(Math.pow(10, logValue));
  }

  /**
   * Update step control options based on current scale
   */
  updateStepOptions() {
    const stepSelect = document.getElementById('sigma-step');
    if (!stepSelect) return;

    const isLog = AppState.sigmaScale === 'logarithmic';
    const currentValue = stepSelect.value;

    // Clear existing options
    stepSelect.innerHTML = '';

    if (isLog) {
      const options = [
        { value: '1.02', label: '1.02x' },
        { value: '1.05', label: '1.05x' },
        { value: '1.1', label: '1.1x' },
        { value: '1.2', label: '1.2x' }
      ];
      options.forEach(opt => {
        const o = document.createElement('option');
        o.value = opt.value;
        o.textContent = opt.label;
        if (opt.value === '1.05') o.selected = true; // Default for log
        stepSelect.appendChild(o);
      });
    } else {
      const options = [
        { value: '1', label: '1' },
        { value: '5', label: '5' },
        { value: '10', label: '10' },
        { value: '20', label: '20' }
      ];
      options.forEach(opt => {
        const o = document.createElement('option');
        o.value = opt.value;
        o.textContent = opt.label;
        if (opt.value === '5') o.selected = true; // Default for linear
        stepSelect.appendChild(o);
      });
    }
  }

  /**
   * Set up sigma scale toggle and slider sync
   */
  setupSigmaScaleHandler() {
    const linearSlider = document.getElementById('sigma-slider');
    const logSlider = document.getElementById('sigma-slider-log');
    const linearScale = document.getElementById('sigma-linear-scale');
    const logScale = document.getElementById('sigma-log-scale');
    const sigmaDisplay = document.getElementById('sigma-display');

    if (!linearSlider || !logSlider || !linearScale || !logScale) return;

    // Linear slider input
    linearSlider.addEventListener('input', () => {
      if (linearScale.checked) {
        const val = parseFloat(linearSlider.value);
        logSlider.value = this.sigmaLinearToLog(val);
        AppState.sigmaValue = val;
        sigmaDisplay.textContent = Math.round(val);
        this.updateLayersBySigma(val);
      }
    });

    // Logarithmic slider input
    logSlider.addEventListener('input', () => {
      if (logScale.checked) {
        const linearVal = this.sigmaLogToLinear(logSlider.value);
        linearSlider.value = linearVal;
        AppState.sigmaValue = linearVal;
        sigmaDisplay.textContent = Math.round(linearVal);
        this.updateLayersBySigma(linearVal);
      }
    });

    // Toggle to Linear
    linearScale.addEventListener('change', () => {
      if (linearScale.checked) {
        linearSlider.style.display = 'block';
        logSlider.style.display = 'none';
        AppState.sigmaScale = 'linear';
        this.updateStepOptions();
      }
    });

    // Toggle to Logarithmic
    logScale.addEventListener('change', () => {
      if (logScale.checked) {
        linearSlider.style.display = 'none';
        logSlider.style.display = 'block';
        AppState.sigmaScale = 'logarithmic';
        logSlider.value = this.sigmaLinearToLog(linearSlider.value);
        this.updateStepOptions();
      }
    });

    // Initial step options setup
    this.updateStepOptions();

    // Handle snaps for log slider too
    logSlider.addEventListener('mouseup', () => {
      if (!AppState.isPlaying && logScale.checked) {
        const nearest = findNearestSigma(AppState.sigmaValue);
        AppState.sigmaValue = nearest;
        linearSlider.value = nearest;
        logSlider.value = this.sigmaLinearToLog(nearest);
        sigmaDisplay.textContent = nearest;
        this.updateLayersBySigma(nearest);
      }
    });

    // Handle snaps for log slider too (touch)
    logSlider.addEventListener('touchend', () => {
      if (!AppState.isPlaying && logScale.checked) {
        const nearest = findNearestSigma(AppState.sigmaValue);
        AppState.sigmaValue = nearest;
        linearSlider.value = nearest;
        logSlider.value = this.sigmaLinearToLog(nearest);
        sigmaDisplay.textContent = nearest;
        this.updateLayersBySigma(nearest);
      }
    });
  }

  /**
   * Update POI value display and filters
   */
  updatePoiValue() {
    const linearSlider = document.getElementById('poi-slider');
    const logSlider = document.getElementById('poi-slider-log');
    const linearScale = document.getElementById('linear-scale');
    const logScale = document.getElementById('log-scale');
    const poiValue = document.getElementById('poi-value');

    let minPois;

    // Determine which slider is active and use its value
    if (logScale.checked) {
      // When log scale is active, convert the log slider value to linear
      const logValue = parseFloat(logSlider.value);

      // Standard logarithmic conversion
      if (logValue === 0) {
        minPois = 0;
      } else {
        minPois = Math.round(Math.pow(10, logValue) - 1);
      }
    } else {
      // When linear scale is active, use linear slider value directly
      minPois = parseInt(linearSlider.value, 10);
    }

    // Update the value display in the header
    poiValue.textContent = minPois.toString();

    // Update AppState
    AppState.populationFilter = minPois;

    // Apply filters
    this.mapController.updatePointsFilter(minPois);
    this.mapController.updateBoundaryFilter(minPois);
  }

  /**
   * Set up time slider control handlers
   */
  setupTimeSliderHandlers() {
    const sigmaSlider = document.getElementById('sigma-slider');
    const sigmaDisplay = document.getElementById('sigma-display');
    const playPauseBtn = document.getElementById('play-pause-btn');
    const playbackSpeedSelect = document.getElementById('playback-speed'); // Get the speed select element
    const minInput = document.getElementById('sigma-min');
    const maxInput = document.getElementById('sigma-max');

    // Add keyboard navigation animation state
    let keyboardAnimationId = null;
    let keyboardAnimationStartTime = null;
    let keyboardAnimationStartSigma = null;
    let keyboardAnimationTargetSigma = null;
    const KEYBOARD_ANIMATION_DURATION = 500; // Duration in milliseconds

    // Use arrow function to preserve the 'this' context
    const animateToSigma = (timestamp) => {
      if (!keyboardAnimationStartTime) {
        keyboardAnimationStartTime = timestamp;
      }

      const elapsed = timestamp - keyboardAnimationStartTime;
      const progress = Math.min(elapsed / KEYBOARD_ANIMATION_DURATION, 1);

      // Ease in-out function
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      // Calculate current sigma
      const currentSigma = keyboardAnimationStartSigma + (keyboardAnimationTargetSigma - keyboardAnimationStartSigma) * easeProgress;

      // Update sliders and display
      sigmaSlider.value = currentSigma;
      const logSlider = document.getElementById('sigma-slider-log');
      if (logSlider) {
        logSlider.value = this.sigmaLinearToLog(currentSigma);
      }
      sigmaDisplay.textContent = Math.round(currentSigma);
      AppState.sigmaValue = currentSigma;
      this.updateLayersBySigma(currentSigma);

      // Continue animation if not complete
      if (progress < 1) {
        keyboardAnimationId = requestAnimationFrame(animateToSigma);
      } else {
        // Clean up animation state
        keyboardAnimationId = null;
        keyboardAnimationStartTime = null;
        keyboardAnimationStartSigma = null;
        keyboardAnimationTargetSigma = null;
      }
    };

    const handleKeydown = (e) => {
      // Only handle left/right arrow keys
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      // Prevent default slider behavior
      e.preventDefault();

      // If animation is already running, complete it immediately
      if (keyboardAnimationId) {
        cancelAnimationFrame(keyboardAnimationId);
        sigmaSlider.value = keyboardAnimationTargetSigma;
        AppState.sigmaValue = keyboardAnimationTargetSigma;
        this.updateLayersBySigma(keyboardAnimationTargetSigma);
      }

      const isLog = AppState.sigmaScale === 'logarithmic';
      const currentSigma = AppState.sigmaValue;
      const stepSelect = document.getElementById('sigma-step');
      const stepVal = parseFloat(stepSelect.value) || 1;
      const minSigma = 1;
      const maxSigma = 500;

      let targetSigma;

      if (isLog) {
        // Multiplier-based increment
        if (e.key === 'ArrowLeft') {
          targetSigma = currentSigma / stepVal;
        } else {
          targetSigma = currentSigma * stepVal;
        }
      } else {
        if (e.key === 'ArrowLeft') {
          targetSigma = currentSigma - stepVal;
        } else {
          targetSigma = currentSigma + stepVal;
        }
      }

      // Bounds and wrap around
      if (targetSigma < minSigma) targetSigma = maxSigma;
      if (targetSigma > maxSigma) targetSigma = minSigma;

      // Start the animation
      keyboardAnimationStartSigma = currentSigma;
      keyboardAnimationTargetSigma = targetSigma;
      keyboardAnimationStartTime = null;
      keyboardAnimationId = requestAnimationFrame(animateToSigma);
    };

    // Add keyboard navigation for both sliders
    sigmaSlider.addEventListener('keydown', handleKeydown);
    const logSlider = document.getElementById('sigma-slider-log');
    if (logSlider) {
      logSlider.addEventListener('keydown', handleKeydown);
    }

    // Update sigma display and layer opacities when slider changes
    sigmaSlider.addEventListener('input', (e) => {
      // Handled in setupSigmaScaleHandler
    });

    // Make the slider snap to available data sigma values when user releases it
    sigmaSlider.addEventListener('mouseup', () => {
      // Only snap if we're not in animation mode
      if (!AppState.isPlaying) {
        const nearest = findNearestSigma(AppState.sigmaValue);

        // Snap to that sigma
        AppState.sigmaValue = nearest;
        sigmaSlider.value = AppState.sigmaValue;
        sigmaDisplay.textContent = AppState.sigmaValue;
        this.updateLayersBySigma(AppState.sigmaValue);
      }
    });

    // Also handle touch events for mobile
    sigmaSlider.addEventListener('touchend', () => {
      // Only snap if we're not in animation mode
      if (!AppState.isPlaying) {
        const nearest = findNearestSigma(AppState.sigmaValue);

        // Snap to that sigma
        AppState.sigmaValue = nearest;
        sigmaSlider.value = AppState.sigmaValue;
        sigmaDisplay.textContent = AppState.sigmaValue;
        this.updateLayersBySigma(AppState.sigmaValue);
      }
    });

    // Play/Pause button functionality
    // Play/Pause button functionality
    playPauseBtn.addEventListener('click', () => {
      if (AppState.isPlaying) {
        this.stopSigmaAnimation();
        playPauseBtn.textContent = '\u25B6'; // Play symbol
        AppState.isPlaying = false;
      } else {
        this.startSigmaAnimation();
        playPauseBtn.textContent = '\u23F8'; // Pause symbol
        AppState.isPlaying = true;
      }
    });

    // Add event listener for playback speed changes
    if (playbackSpeedSelect) {
      playbackSpeedSelect.addEventListener('change', (e) => {
        AppState.animationSpeed = parseFloat(e.target.value);
      });
      // Initialize AppState.animationSpeed with the current value on load
      AppState.animationSpeed = parseFloat(playbackSpeedSelect.value);
    }

    // Min/Max input handlers
    if (minInput) {
      minInput.addEventListener('change', (e) => {
        let val = parseInt(e.target.value, 10);
        if (isNaN(val)) val = 1;
        if (val < 1) val = 1;
        if (val > AppState.maxSigma) val = AppState.maxSigma;

        AppState.minSigma = val;
        e.target.value = val;
      });
    }

    if (maxInput) {
      maxInput.addEventListener('change', (e) => {
        let val = parseInt(e.target.value, 10);
        if (isNaN(val)) val = 500;
        if (val > 500) val = 500;
        if (val < AppState.minSigma) val = AppState.minSigma;

        AppState.maxSigma = val;
        e.target.value = val;
      });
    }
  }

  /**
   * Handle sigma toggle checkbox changes
   */
  // handleSigmaToggle, handleOpacitySlider, and redistributeOpacity removed.

  /**
   * Update population value display and filters
   */
  updatePopulationValue() {
    const linearSlider = document.getElementById('population-slider');
    const logSlider = document.getElementById('population-slider-log');
    const linearScale = document.getElementById('linear-scale');
    const logScale = document.getElementById('log-scale');
    const populationValue = document.getElementById('population-value');

    let minPop;

    // Determine which slider is active and use its value
    if (logScale.checked) {
      // When log scale is active, convert the log slider value to linear
      const logValue = parseFloat(logSlider.value);

      // Standard logarithmic conversion
      if (logValue === 0) {
        minPop = 0;
      } else {
        minPop = Math.round(Math.pow(10, logValue) - 1);
      }
    } else {
      // When linear scale is active, use linear slider value directly
      minPop = parseInt(linearSlider.value, 10);
    }

    // Update the value display in the header
    const formattedPop = formatPopulation(minPop);
    populationValue.textContent = formattedPop;

    // Apply filters
    this.mapController.updatePointsFilter(minPop);
    this.mapController.updateBoundaryFilter(minPop);
  }

  /**
   * Start sigma animation
   */
  startSigmaAnimation() {
    // Robustness check: if for some reason ID exists but we are starting, clear it
    if (AppState.animationFrameId) {
      cancelAnimationFrame(AppState.animationFrameId);
      AppState.animationFrameId = null;
    }

    // Ensure start value is within bounds
    if (AppState.sigmaValue < AppState.minSigma || AppState.sigmaValue > AppState.maxSigma) {
      AppState.sigmaValue = AppState.minSigma;
      document.getElementById('sigma-slider').value = AppState.minSigma;
      this.updateLayersBySigma(AppState.minSigma);
    }

    AppState.lastTimestamp = performance.now();
    AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
  }

  /**
   * Stop sigma animation
   */
  stopSigmaAnimation() {
    if (AppState.animationFrameId) {
      cancelAnimationFrame(AppState.animationFrameId);
      AppState.animationFrameId = null;
    }

    // Reset pause state when stopping animation
    AppState.isPaused = false;

    // Explicitly update play button text
    const playPauseBtn = document.getElementById('play-pause-btn');
    if (playPauseBtn) {
      playPauseBtn.textContent = '\u25B6'; // Play symbol
    }

    // Snap to the active sigma value (the one with opacity > 0.5)
    const activeSigma = AppState.getActiveSigma();
    AppState.sigmaValue = activeSigma;
    const sigmaSlider = document.getElementById('sigma-slider');
    if (sigmaSlider) {
      sigmaSlider.value = activeSigma;
    }

    // Reset all layer opacities in AppState
    CONFIG.sigmaValues.forEach(sigma => {
      AppState.sigmaOpacities[sigma] = sigma === activeSigma ? 1 : 0;
    });

    // Update actual layer opacities
    this.mapController.updateAllLayersOpacity();

    // Make sure the display reflects the active sigma
    const sigmaDisplay = document.getElementById('sigma-display');
    if (sigmaDisplay) {
      sigmaDisplay.textContent = activeSigma;
    }
  }

  /**
   * Animate sigma slider
   */
  /**
   * Animate sigma slider
   */
  animateSigma(timestamp) {
    const sigmaSlider = document.getElementById('sigma-slider');
    const sigmaDisplay = document.getElementById('sigma-display');
    const playbackSpeedSelect = document.getElementById('playback-speed');
    const playPauseBtn = document.getElementById('play-pause-btn');

    // Handle the pause at the end of each cycle
    if (AppState.isPaused) {
      // Check if we've been paused long enough
      if (timestamp - AppState.pauseStartTime >= CONFIG.animation.duration) {
        // Resume animation and reset to the minimum sigma
        AppState.isPaused = false;
        AppState.sigmaValue = AppState.minSigma; // Use custom min
        sigmaSlider.value = AppState.sigmaValue;
        this.updateLayersBySigma(AppState.sigmaValue);
      }
      // Continue the animation loop but don't update while paused
      AppState.lastTimestamp = timestamp;
      AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
      return;
    }

    const elapsed = timestamp - AppState.lastTimestamp;
    const playbackSpeed = parseFloat(playbackSpeedSelect.value);

    // Configurable animation speed (steps per second base rate)
    // Adjust this value as needed. 1 step/sec base feels reasonable?
    // Using existing config implies sigmasPerSecond, but with steps it's different.
    // Let's assume we want to update N times per second.
    const updateInterval = 1000 / (1 * playbackSpeed); // Updates 1 time per second at 1x speed

    if (elapsed >= updateInterval) {
      const isLog = AppState.sigmaScale === 'logarithmic';
      const stepSelect = document.getElementById('sigma-step');
      const stepVal = parseFloat(stepSelect.value) || 1;

      // Increment value
      if (isLog) {
        AppState.sigmaValue *= stepVal;
      } else {
        AppState.sigmaValue += stepVal;
      }

      // If we reached the max, start pause before looping back
      if (AppState.sigmaValue > AppState.maxSigma) { // Use custom max
        // Set to exactly max value
        AppState.sigmaValue = AppState.maxSigma;
      }

      // Update the sliders and layer opacities
      sigmaSlider.value = AppState.sigmaValue;
      document.getElementById('sigma-slider-log').value = this.sigmaLinearToLog(AppState.sigmaValue);
      sigmaDisplay.textContent = Math.round(AppState.sigmaValue);
      this.updateLayersBySigma(AppState.sigmaValue);

      if (AppState.sigmaValue >= AppState.maxSigma && !AppState.isPaused) {
        // Start pause
        AppState.isPaused = true;
        AppState.pauseStartTime = timestamp;
      }

      AppState.lastTimestamp = timestamp;
    }

    // Continue loop
    if (AppState.isPlaying) {
      AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
    } else {
      // Ensure button state is correct if stopped externally
      if (playPauseBtn) playPauseBtn.textContent = '\u25B6';
      AppState.animationFrameId = null; // Clear frame ID so it can be restarted
    }
  }

  /**
   * Update layer opacities based on sigma value
   */
  updateLayersBySigma(sigma) {
    // Find the nearest sigma from the predefined list
    const nearestSigma = findNearestSigma(sigma);

    // Reset current opacities in AppState
    Object.keys(AppState.sigmaOpacities).forEach(s => {
      AppState.sigmaOpacities[s] = 0;
    });

    // Set opacity for the nearest sigma to 1.0
    AppState.sigmaOpacities[nearestSigma] = 1;

    // Update actual layer opacities via map controller
    this.mapController.updateAllLayersOpacity();

    // Update display with the active sigma value
    const sigmaDisplay = document.getElementById('sigma-display');
    if (sigmaDisplay) {
      sigmaDisplay.textContent = nearestSigma;
    }
  }

  /**
   * Set up location search handlers
   */
  setupLocationSearchHandlers() {
    const searchInput = document.getElementById('location-search');
    const searchButton = document.getElementById('search-button');
    const searchResults = document.getElementById('search-results');

    // Search button click handler
    searchButton.addEventListener('click', () => {
      this.performSearch(searchInput.value);
    });

    // Enter key in search input
    searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.performSearch(searchInput.value);
      }
    });

    // Clear results when input changes
    searchInput.addEventListener('input', () => {
      searchResults.innerHTML = '';
      searchResults.style.display = 'none';
    });
  }

  /**
   * Perform location search
   * @param {string} query - Search query
   */
  performSearch(query) {
    if (!query || query.trim() === '') return;

    const searchResults = document.getElementById('search-results');
    searchResults.innerHTML = '<div class="search-result">Searching...</div>';
    searchResults.style.display = 'block';

    this.mapController.searchLocation(query)
      .then(results => {
        searchResults.innerHTML = '';

        if (results.length === 0) {
          searchResults.innerHTML = '<div class="search-result">No results found</div>';
          return;
        }

        // Limit to top 5 results
        const topResults = results.slice(0, 5);

        topResults.forEach(result => {
          const resultElement = document.createElement('div');
          resultElement.className = 'search-result';
          resultElement.innerHTML = `${result.display_name}`;

          resultElement.addEventListener('click', () => {
            this.mapController.zoomToLocation(result);
            searchResults.innerHTML = '';
            searchResults.style.display = 'none';
          });

          searchResults.appendChild(resultElement);
        });

        // If only one result, automatically zoom to it
        if (results.length === 1) {
          this.mapController.zoomToLocation(results[0]);
          setTimeout(() => {
            searchResults.innerHTML = '';
            searchResults.style.display = 'none';
          }, 1000);
        }
      })
      .catch(error => {
        console.error('Search error:', error);
        searchResults.innerHTML = '<div class="search-result">Error searching location</div>';
      });
  }

  /**
   * Toggle population panel visibility
   */
  togglePopulationPanel() {
    const header = document.getElementById('population-header');
    const content = document.getElementById('population-content');

    if (header && content) {
      header.classList.toggle('active');
      content.classList.toggle('expanded');
    }
  }


  /**
   * Set up preset selector handler
   */
  setupPresetHandler() {
    const presetSubcentres = document.getElementById('preset-subcentres');
    const presetCities = document.getElementById('preset-cities');

    // Function to update sigma only (cellsize fixed at 100)
    const updateParameters = (sigma) => {
      // Update sigma slider and display
      if (CONFIG.sigmaValues.includes(sigma)) {
        const sigmaSlider = document.getElementById('sigma-slider');
        if (sigmaSlider) {
          sigmaSlider.value = sigma;
        }

        const sigmaDisplay = document.getElementById('sigma-display');
        if (sigmaDisplay) {
          sigmaDisplay.textContent = sigma.toString();
        }

        // Update layer opacities based on the new sigma
        AppState.sigmaValue = sigma;
        const logSlider = document.getElementById('sigma-slider-log');
        if (logSlider) {
          logSlider.value = this.sigmaLinearToLog(sigma);
        }
        this.updateLayersBySigma(sigma);
      }
    };

    // Add change handlers for preset radio buttons
    if (presetSubcentres) {
      presetSubcentres.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Subcentres: sigma 5
          updateParameters(5);
        }
      });
    }

    if (presetCities) {
      presetCities.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Cities: sigma 114
          updateParameters(114);
        }
      });
    }

    const updatePresetSelection = () => {
      const currentSigma = AppState.getActiveSigma();

      // Check if current settings match any preset
      if (currentSigma === 5) {
        presetSubcentres.checked = true;
      } else if (currentSigma === 114) {
        presetCities.checked = true;
      } else {
        // If no preset matches, uncheck both preset radios
        presetSubcentres.checked = false;
        presetCities.checked = false;
      }
    };

    // Listen for changes to sigma
    // Note: cellSizeRadios logic removed as Cell Size is fixed

    const sigmaSlider = document.getElementById('sigma-slider');
    if (sigmaSlider) {
      sigmaSlider.addEventListener('change', updatePresetSelection);
    } else {
      console.error("Sigma slider element with ID 'sigma-slider' not found in setupPresetHandler.");
    }

    // Initial preset selection based on current state
    updatePresetSelection();
  }
}

export default UIController;